/**
 * Legal Modal Component
 *
 * Displays Terms and Conditions or Privacy Policy in a modal overlay.
 * Used when the user clicks footer links so they stay on the current page.
 * Content is loaded via iframe from the same app routes (/terms, /privacy).
 */

import { Box } from '@mantine/core';
import { useModalZIndex } from '../../hooks/useModalZIndex';
import { AppModal } from './AppModal';

export type LegalModalType = 'terms' | 'privacy';

export interface LegalModalProps {
  /** Which legal document to show */
  type: LegalModalType | null;
  /** Whether the modal is open (type is non-null) */
  opened: boolean;
  /** Callback when the modal is closed */
  onClose: () => void;
}

const titles: Record<LegalModalType, string> = {
  terms: 'Terms and Conditions',
  privacy: 'Privacy Policy',
};

/**
 * Modal that shows Terms or Privacy content in a scrollable iframe.
 * Footer links open this instead of navigating away.
 */
export const LegalModal = ({ type, opened, onClose }: LegalModalProps) => {
  const zIndex = useModalZIndex(opened);

  if (!type) return null;

  const src = type === 'terms' ? '/terms' : '/privacy';

  return (
    <AppModal
      id="legal"
      opened={opened}
      onClose={onClose}
      title={titles[type]}
      zIndex={zIndex}
      size="xl"
      noScrollArea
      footer={null}
      styles={{
        title: { fontSize: '1.25rem', fontWeight: 600 },
      }}
    >
      <Box
        component="iframe"
        src={src}
        title={titles[type]}
        style={{
          width: '100%',
          height: '100%',
          minHeight: 400,
          border: 'none',
          display: 'block',
        }}
      />
    </AppModal>
  );
};
